"use client"

import Image from "next/image"
import Link from "next/link"
import { useEffect, useState } from "react"

const navItems = [
  { label: "Sobre Nosotros", href: "#valores" },
  { label: "Catálogo", href: "#catalogo" },
  { label: "Proceso", href: "#proceso" },
  { label: "Sustentabilidad", href: "#sustentabilidad" },
  { label: "FAQ", href: "#faq" },
]

const buttonBase =
  "inline-flex items-center justify-center rounded-[2px] font-medium text-[clamp(12px,1.16vw,22px)] leading-[clamp(14px,1.4vw,26px)] h-[clamp(30px,2.9vw,56px)] px-[clamp(18px,1.7vw,32px)] touch-manipulation transition-opacity duration-[200ms] ease-[cubic-bezier(0.2,0.8,0.2,1)] hover:opacity-90 motion-reduce:transition-none motion-reduce:hover:opacity-100 focus-visible:outline focus-visible:outline-[2px] focus-visible:outline-offset-[2px] cursor-pointer"

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  useEffect(() => {
    const closeOnDesktop = () => {
      if (window.innerWidth >= 768) {
        setMobileMenuOpen(false)
      }
    }

    window.addEventListener("resize", closeOnDesktop)
    return () => window.removeEventListener("resize", closeOnDesktop)
  }, [])

  return (
    <header
      id="header"
      className="w-full border-b-1 border-b-[color:var(--color-red)] bg-[color:var(--color-white)] fixed top-0 left-0 right-0 z-50"
    >
      <div className="flex items-center justify-between px-[max(16px,calc(16px+var(--safe-area-left)))] py-3 pr-[max(16px,calc(16px+var(--safe-area-right)))] md:px-10 md:py-0">
        <Link href="/" aria-label="Vacavaliente" className="block">
          <Image
            src="/images/logo.png"
            alt="Vacavaliente"
            width={211}
            height={25}
            priority
            className="h-auto w-[152px] md:w-[211px]"
          />
        </Link>

        <button
          type="button"
          aria-label={mobileMenuOpen ? "Cerrar menú" : "Abrir menú"}
          aria-expanded={mobileMenuOpen}
          aria-controls="mobile-main-nav"
          onClick={() => setMobileMenuOpen((current) => !current)}
          className="inline-flex h-10 w-10 items-center justify-center rounded-[2px]focus-visible:outline focus-visible:outline-[2px] focus-visible:outline-offset-[2px] focus-visible:outline-[color:var(--color-black)] md:hidden"
        >
          <span aria-hidden="true" className="relative block h-[14px] w-[18px]">
            <span
              className={`absolute left-0 top-0 h-[2px] w-full bg-current transition-all duration-200 ease-out ${
                mobileMenuOpen ? "top-1/2 -translate-y-1/2 rotate-45" : ""
              }`}
            />
            <span
              className={`absolute left-0 top-1/2 h-[2px] w-full -translate-y-1/2 bg-current transition-opacity duration-150 ease-out ${
                mobileMenuOpen ? "opacity-0" : "opacity-100"
              }`}
            />
            <span
              className={`absolute bottom-0 left-0 h-[2px] w-full bg-current transition-all duration-200 ease-out ${
                mobileMenuOpen ? "bottom-1/2 translate-y-1/2 -rotate-45" : ""
              }`}
            />
          </span>
        </button>

        <nav
          aria-label="Principal"
          className="hidden justify-self-center md:block"
        >
          <ul className="flex items-center gap-[32px] text-[16px] leading-[20px] font-medium text-[color:var(--color-black)]">
            {navItems.map((item) => (
              <li key={item.label}>
                <Link
                  href={item.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className="touch-manipulation transition-opacity duration-[200ms] ease-[cubic-bezier(0.2,0.8,0.2,1)] hover:opacity-70 motion-reduce:transition-none motion-reduce:hover:opacity-100 focus-visible:outline focus-visible:outline-[2px] focus-visible:outline-offset-[4px] focus-visible:outline-[color:var(--color-black)]"
                >
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
        <div className="hidden items-center gap-[12px] md:flex">
          <Link
            href="/docs/Catalogo-2026.pdf"
            target="_blank"
            rel="noreferrer"
            className={`${buttonBase} bg-[color:var(--color-red)] text-[color:var(--color-white)] focus-visible:outline-[color:var(--color-black)]`}
          >
            ver catálogo
          </Link>
          <Link
            href="#contact"
            className={`${buttonBase} border border-[color:var(--color-black)] text-[color:var(--color-black)] focus-visible:outline-[color:var(--color-black)]`}
          >
            contactate
          </Link>
        </div>
      </div>

      <div
        id="mobile-main-nav"
        className={`overflow-hidden border-t border-[color:var(--color-red)] transition-[max-height,opacity] duration-200 ease-out md:hidden ${mobileMenuOpen ? "max-h-[520px] opacity-100" : "max-h-0 opacity-0"}`}
      >
        <nav
          aria-label="Principal móvil"
          className="px-[max(16px,calc(16px+var(--safe-area-left)))] py-4 pr-[max(16px,calc(16px+var(--safe-area-right)))]"
        >
          <ul className="flex flex-col gap-3 text-[16px] leading-[20px] font-medium text-[color:var(--color-black)]">
            {navItems.map((item) => (
              <li key={`mobile-${item.label}`}>
                <Link
                  href={item.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className="inline-flex rounded-[2px] py-1 focus-visible:outline focus-visible:outline-[2px] focus-visible:outline-offset-[2px] focus-visible:outline-[color:var(--color-black)]"
                >
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
          <div className="mt-5 flex flex-col gap-2">
            <Link
              href="/docs/Catalogo-2026.pdf"
              target="_blank"
              rel="noreferrer"
              onClick={() => setMobileMenuOpen(false)}
              className={`${buttonBase} w-full bg-[color:var(--color-red)] text-[color:var(--color-white)] focus-visible:outline-[color:var(--color-black)]`}
            >
              ver catálogo
            </Link>
            <Link
              href="#contact"
              onClick={() => setMobileMenuOpen(false)}
              className={`${buttonBase} w-full border border-[color:var(--color-black)] text-[color:var(--color-black)] focus-visible:outline-[color:var(--color-black)]`}
            >
              contactate
            </Link>
          </div>
        </nav>
      </div>
    </header>
  )
}
